import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, Award, FileText, Star, Users, MessageCircle, Loader2 
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MemberProfileCard({ 
  userId, 
  onMessage = null,
  compact = false,
  className = '' 
}) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (userId) {
      loadProfile();
    }
  }, [userId]);

  const loadProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/member/${userId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setFollowing(data.is_following);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFollow = async () => {
    if (!token) {
      toast.error('Please log in to follow members');
      return;
    }

    setFollowLoading(true);
    try {
      const method = following ? 'DELETE' : 'POST';
      const res = await fetch(`${API_URL}/api/social/follow/${userId}`, {
        method,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setFollowing(!following);
        setProfile(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            follower_count: following 
              ? prev.stats.follower_count - 1 
              : prev.stats.follower_count + 1
          }
        }));
        toast.success(following ? 'Unfollowed' : 'Following');
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-[#0d1525] rounded-xl border border-white/10 p-6 ${className}`}>
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`bg-[#0d1525] rounded-xl border border-white/10 p-6 text-center text-gray-400 ${className}`}>
        <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p>Member not found</p>
      </div>
    );
  }

  // Compact version for lists
  if (compact) {
    return (
      <div className={`flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors ${className}`}>
        {profile.avatar ? (
          <img 
            src={profile.avatar} 
            alt={profile.name}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-white truncate">{profile.name}</p>
            {profile.is_ai_certified && (
              <Award className="w-4 h-4 text-amber-400 flex-shrink-0" />
            )}
          </div>
          {profile.company_name && (
            <p className="text-sm text-gray-400 truncate">{profile.company_name}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onMessage && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onMessage(profile)}
              className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
          )}
          {token && (
            <Button
              variant={following ? 'default' : 'outline'}
              size="sm"
              onClick={toggleFollow}
              disabled={followLoading}
              className={following 
                ? 'bg-cyan-500 hover:bg-cyan-600 text-white' 
                : 'border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10'
              }
            >
              {followLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (following ? 'Following' : 'Follow')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Full card version
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-[#0d1525] rounded-xl border border-white/10 overflow-hidden ${className}`}
      data-testid={`member-profile-${userId}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500/20 to-teal-500/20 p-6">
        <div className="flex items-start gap-4">
          {profile.avatar ? (
            <img 
              src={profile.avatar} 
              alt={profile.name}
              className="w-20 h-20 rounded-full object-cover border-4 border-[#0d1525]"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center border-4 border-[#0d1525]">
              <User className="w-10 h-10 text-white" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-white">{profile.name}</h3>
              {profile.is_ai_certified && (
                <div className="px-2 py-0.5 bg-amber-500/20 rounded-full flex items-center gap-1">
                  <Award className="w-3 h-3 text-amber-400" />
                  <span className="text-xs text-amber-400 font-medium">AI Certified</span>
                </div>
              )}
            </div>
            {profile.company_name && (
              <p className="text-gray-400 text-sm">{profile.company_name}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Member since {new Date(profile.joined_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 border-b border-white/10">
        <div className="p-4 text-center border-r border-white/10">
          <p className="text-lg font-bold text-white">{profile.stats.content_count}</p>
          <p className="text-xs text-gray-400">Posts</p>
        </div>
        <div className="p-4 text-center border-r border-white/10">
          <p className="text-lg font-bold text-white">{profile.stats.review_count}</p>
          <p className="text-xs text-gray-400">Reviews</p>
        </div>
        <div className="p-4 text-center border-r border-white/10">
          <p className="text-lg font-bold text-white">{profile.stats.follower_count}</p>
          <p className="text-xs text-gray-400">Followers</p>
        </div>
        <div className="p-4 text-center">
          <p className="text-lg font-bold text-white">{profile.stats.following_count}</p>
          <p className="text-xs text-gray-400">Following</p>
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 flex items-center gap-3">
        {token && (
          <>
            <Button
              variant={following ? 'default' : 'outline'}
              onClick={toggleFollow}
              disabled={followLoading}
              className={`flex-1 ${
                following 
                  ? 'bg-cyan-500 hover:bg-cyan-600 text-white' 
                  : 'border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10'
              }`}
            >
              {followLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Users className="w-4 h-4 mr-2" />
              )}
              {following ? 'Following' : 'Follow'}
            </Button>
            {onMessage && (
              <Button
                variant="outline"
                onClick={() => onMessage(profile)}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Message
              </Button>
            )}
          </>
        )}
      </div>

      {/* Recent Content */}
      {profile.recent_content && profile.recent_content.length > 0 && (
        <div className="p-4 border-t border-white/10">
          <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Recent Posts
          </h4>
          <div className="space-y-2">
            {profile.recent_content.slice(0, 3).map(content => (
              <div key={content.id} className="flex items-center gap-2 text-sm">
                <span className="px-2 py-0.5 bg-white/5 rounded text-xs text-gray-400 capitalize">
                  {content.content_type.replace(/_/g, ' ')}
                </span>
                <span className="text-white truncate flex-1">{content.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default MemberProfileCard;
